import {
	GQLMerchDecorationFragment,
	GQLMerchDecorationTypeType,
	GQLOperationalSettingsFragment,
	GQLOrderItemPrintDecorationType,
	GQLOrderItemPrintFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
	GQLProductTileFragment,
	GQLStoreBaseFragment,
} from '@theprintbar/graphql';
import { Decoration, DecorationServer } from '.';
import { getCurrencyValue, getPartnerPortalPrice, invariant } from '../..';
import { getDecorationMeta, isMerchDecoration, isOrderItemPrint } from './utils';

interface DecorationPricingProps {
	settings: GQLOperationalSettingsFragment;
	decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment | null;
	store?: GQLStoreBaseFragment | null;
	product?: Pick<
		GQLProductTileFragment,
		'DTGSurcharge' | 'customDecorationBasePrice' | 'customDecorationName' | 'customerDecorationCostPerColour'
	> | null;
	dontCheckArtwork?: boolean;
}

export const getDecorationPrice = ({ settings, decoration, store, product, dontCheckArtwork }: DecorationPricingProps) => {
	let amount = 0;
	if (
		!decoration ||
		((isOrderItemPrint(decoration)
			? !decoration.artwork?.filesize
			: isMerchDecoration(decoration)
			? !decoration.design
			: !decoration.artwork?.size) &&
			!dontCheckArtwork)
	) {
		return amount;
	}

	if (!!product?.customDecorationName) {
		const { colours } = getDecorationMeta(decoration);
		const colourCount = colours?.length || 0;

		amount += getCurrencyValue(product.customDecorationBasePrice);
		amount += getCurrencyValue(product.customerDecorationCostPerColour) * colourCount;
		return amount;
	}

	const type = isOrderItemPrint(decoration) ? decoration.decoration : decoration.type;

	switch (type) {
		case GQLOrderItemPrintDecorationType.Dtg:
		case GQLMerchDecorationTypeType.Dtg:
		case GQLProductSideAvailableDecorationType.Dtg:
			amount = getCurrencyValue(
				store ? getCurrencyValue(store?.dtgPrintPrice) || settings.dtfPartnerPrintPrice : settings.dtgPrintPrice,
			);
			amount += getCurrencyValue(product?.DTGSurcharge || '0');
			break;
		case GQLOrderItemPrintDecorationType.Dtf:
		case GQLMerchDecorationTypeType.Dtf:
		case GQLProductSideAvailableDecorationType.Dtf:
			const isLabel = isMerchDecoration(decoration)
				? decoration.side?.side === GQLProductSideSideType.Label
				: decoration.side === GQLProductSideSideType.Label;
			amount = isLabel
				? getCurrencyValue(settings.dtfLabelPrintPrice)
				: getCurrencyValue(
						store ? getCurrencyValue(store?.dtfPrintPrice) || settings.dtfPartnerPrintPrice : settings.dtfPrintPrice,
				  );
			break;
		case GQLMerchDecorationTypeType.Scr:
		case GQLOrderItemPrintDecorationType.Scr:
		case GQLProductSideAvailableDecorationType.Scr:
			amount = getScreenPrice(settings, decoration);
			if (store) {
				amount = getPartnerPortalPrice(amount, store);
			}
			break;
		case GQLMerchDecorationTypeType.Emb:
		case GQLOrderItemPrintDecorationType.Emb:
		case GQLProductSideAvailableDecorationType.Emb:
			amount = getEmbroideryPrice(settings, decoration, !!store);
			if (store) {
				amount = getPartnerPortalPrice(amount, store);
			}
			break;
		default:
			throw new Error('Unknown decoration type');
	}

	return getCurrencyValue(amount);
};

export const getScreenPrice = (
	settings: GQLOperationalSettingsFragment,
	decoration: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
) => {
	const side = isMerchDecoration(decoration) ? decoration.side?.side : decoration.side;
	invariant(side, 'No side found for screen printing');

	const { colours, requestedColours } = getDecorationMeta(decoration);
	const colourCount = requestedColours || colours?.length || 0;

	if (!colourCount || colourCount > 6) return 0;

	const priceByColour = [
		settings.scrOneColourPrice,
		settings.scrTwoColourPrice,
		settings.scrThreeColourPrice,
		settings.scrFourColourPrice,
		settings.scrFiveColourPrice,
		settings.scrSixColourPrice,
	];

	return parseFloat(priceByColour[colourCount - 1] || '0');
};

export const getEmbroideryPrice = (
	settings: GQLOperationalSettingsFragment,
	decoration: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
	pod = false,
) => {
	const side = isMerchDecoration(decoration) ? decoration.side?.side : decoration.side;
	invariant(side, 'No side found for screen printing');

	const { stitches } = getDecorationMeta(decoration);
	//Round up to the nearest 1000
	const roundedStitches = Math.ceil(stitches || 0 / 1000);

	if (roundedStitches < 5000) {
		return parseFloat(pod ? settings.embPodPriceUnderFive : settings.embPriceUnderFive);
	} else if (roundedStitches < 10000) {
		return parseFloat(pod ? settings.embPodPriceUnderTen : settings.embPriceUnderTen);
	} else if (roundedStitches < 15000) {
		return parseFloat(pod ? settings.embPodPriceUnderFifteen : settings.embPriceUnderFifteen);
	} else if (roundedStitches < 20000) {
		return parseFloat(pod ? settings.embPodPriceUnderTwenty : settings.embPriceUnderTwenty);
	} else {
		return parseFloat(pod ? settings.embPodPriceOverTwenty : settings.embPriceOverTwenty);
	}
};
